import {useContext} from 'react';

import {Outlet, Route, Routes} from 'react-router-dom';

import {AccountConfirmedGuard} from '../RouteGuards/AccountConfirmedGuard';
import {CompanyGuard} from '../RouteGuards/CompanyGuard';
import {CompanySignedContractGuard} from '../RouteGuards/CompanySignedContractGuard';
import {LoggedInGuard} from '../RouteGuards/LoggedInGuard';

import {ErrorMessage} from '../../components/ErrorMessage/ErrorMessage';
import {NotSignedCompanies} from '../../components/NotSignedCompanies/NotSignedCompanies';
import {CompanyContext} from '../../contexts/CompanyContext/CompanyContext';
import {FeatureFlagContext} from '../../contexts/FeatureFlagContext/FeatureFlagContext';
import {SurveyProvider} from '../../contexts/SurveyContext/SurveyContext';
import CompanyAnalyticsDashboard from '../../features/company/recruiting/dashboard/components/CompanyAnalyticsDashboard';
import {CampaignsAnalyticsDashboard} from '../../pages/company/campaigns/CampaignsAnalyticsDashboard';
import {CampaignsPage} from '../../pages/company/campaigns/CampaignsPage';
import {CompanyCultureSurveyQuestionnairePage} from '../../pages/company/culture/CompanyCultureSurveyQuestionnairePage';
import {CompanyCultureSurveyWelcomePage} from '../../pages/company/culture/CompanyCultureSurveyWelcomePage';
import {CultureAnalyticsPage} from '../../pages/company/culture/CultureAnalyticsPage';
import {CultureComparisonPage} from '../../pages/company/culture/CultureComparisonPage';
import {CompanyCultureSurveys} from '../../pages/company/culture/CultureSurveysPage';
import CompanyProfilePage from '../../pages/company/profile/ProfilePage';
import InboxPage from '../../pages/company/recruiting/InboxPage';
import CompanyRecruitingJobPostingsPage from '../../pages/company/recruiting/JobPostingsPage';
import {MatchDetailsPage} from '../../pages/company/recruiting/MatchDetailsPage';
import {MatchesPage} from '../../pages/company/recruiting/MatchesPage';
import {TalentPoolPage} from '../../pages/company/recruiting/TalentPoolPage';
import {TalentPoolProfilePage} from '../../pages/company/recruiting/TalentPoolProfilePage';
import {CompanyCultureChecksPage} from '../../pages/company/screening/CompanyCultureChecksPage';
import {WorkforceDashboardPage} from '../../pages/company/workforce/analysis/WorkforceDashboardPage';
import {WorkforceDepartmentHealthPage} from '../../pages/company/workforce/analysis/WorkforceDepartmentHealthPage';
import {WorkforceProfileDetailPage} from '../../pages/company/workforce/analysis/WorkforceProfileDetailPage';
import {WorkforceProfilesPage} from '../../pages/company/workforce/analysis/WorkforceProfilesPage';
import {WorkforceTargetProfilesPage} from '../../pages/company/workforce/configuration/WorkforceTargetProfilesPage';
import {ModuleTypes} from '../../types/CompanyProductConfiguration/CompanyModule';
import {isCompanyModuleActive} from '../../utils/companyModuleUtils/isCompanyModuleActive';

export const CompanyRoutesContent = () => {
  const {flags} = useContext(FeatureFlagContext);
  const {productConfiguration} = useContext(CompanyContext);

  const isWorkforceModuleActive = isCompanyModuleActive(
    ModuleTypes.WorkforceModule,
    productConfiguration.data?.companyModules
  );

  return (
    <Routes>
      <Route path='/*'>
        <Route path='culture-survey' element={<CompanyCultureSurveyWelcomePage />} />
        <Route path='culture-survey-questionnaire' element={<CompanyCultureSurveyQuestionnairePage />} />
        <Route element={<LoggedInGuard />}>
          <Route element={<CompanyGuard />}>
            <Route element={<AccountConfirmedGuard />}>
              <Route path='not-signed' element={<NotSignedCompanies />} />
              <Route element={<CompanySignedContractGuard />}>
                <Route path='profile' element={<CompanyProfilePage />} />
                <Route path='culture_check' element={<CompanyCultureChecksPage />} />
                <Route path='campaigns' element={<CampaignsPage />} />
                <Route path='campaigns/analytics/:id' element={<CampaignsAnalyticsDashboard />} />
                <Route path='analytics' element={<CompanyAnalyticsDashboard />} />
                {flags.JOB_POSTING_PAGE && (
                  <Route path='recruiting/job-postings' element={<CompanyRecruitingJobPostingsPage />} />
                )}
                <Route path='recruiting/matches' element={<MatchesPage />} />
                <Route path='recruiting/talent-pool/profile/:id' element={<TalentPoolProfilePage />} />
                <Route path='recruiting/talent-pool' element={<TalentPoolPage />} />
                <Route path='recruiting/match/:id' element={<MatchDetailsPage />} />
                {flags.INBOX_PAGE && <Route path='recruiting/inbox' element={<InboxPage />} />}
                {isWorkforceModuleActive && (
                  <Route path='workforce'>
                    <Route path='analysis/dashboard' element={<WorkforceDashboardPage />} />
                    <Route path='analysis/profiles/:id' element={<WorkforceProfileDetailPage />} />
                    <Route path='analysis/profiles' element={<WorkforceProfilesPage />} />
                    <Route path='analysis/department-health' element={<WorkforceDepartmentHealthPage />} />
                    <Route path='configuration/target-profiles' element={<WorkforceTargetProfilesPage />} />
                  </Route>
                )}
                <Route
                  path='culture'
                  element={
                    <SurveyProvider>
                      <Outlet />
                    </SurveyProvider>
                  }
                >
                  <Route path='analytics' element={<CultureAnalyticsPage />} />
                  <Route path='surveys' element={<CompanyCultureSurveys />} />
                  <Route path='comparison' element={<CultureComparisonPage />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path='*' element={<ErrorMessage isOutOfBounds />} />
      </Route>
    </Routes>
  );
};
